<script setup lang="ts">
import { useAuthStore } from '../stores/auth'
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'

const authStore = useAuthStore()
const router = useRouter()

onMounted(async () => {
  // Check if already logged in
  await authStore.checkAuth()
  if (authStore.isAuthenticated) {
    router.push(authStore.needsInvite ? '/invite' : '/')
  }
})
</script>

<template>
  <div class="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
       style="background: linear-gradient(135deg, #1a3a0e 0%, #2d5016 40%, #3d6b1e 100%);">
    <!-- Subtle texture overlay for depth -->
    <div class="absolute inset-0 opacity-[0.04]"
         style="background-image: url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.4%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E');">
    </div>

    <div class="card max-w-md w-full text-center relative z-10">
      <!-- SVG cabin icon replacing emoji -->
      <div class="mb-6">
        <svg class="w-16 h-16 mx-auto" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Vermont Cabin logo">
          <path d="M32 8L4 32h8v20h36V32h8L32 8z" fill="#2d5016" stroke="#1a3a0e" stroke-width="1.5"/>
          <rect x="26" y="36" width="12" height="16" rx="1" fill="#8b4513"/>
          <rect x="28" y="38" width="8" height="6" rx="0.5" fill="#d4a574" opacity="0.6"/>
          <rect x="14" y="24" width="8" height="6" rx="1" fill="#d4a574" opacity="0.4"/>
          <rect x="42" y="24" width="8" height="6" rx="1" fill="#d4a574" opacity="0.4"/>
          <rect x="40" y="12" width="5" height="14" fill="#6b4423"/>
          <path d="M32 8L8 28.5C12 27 22 25 32 25s20 2 24 3.5L32 8z" fill="white" opacity="0.15"/>
        </svg>
      </div>

      <h1 class="text-3xl font-bold text-gray-800 mb-2 heading-serif">Vermont Cabin</h1>
      <p class="text-gray-500 mb-8">Sign in to view the calendar and book your stay</p>

      <div class="space-y-3">
        <button
          @click="authStore.loginWithGoogle"
          class="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border border-cabin-warm rounded-lg hover:bg-cabin-sand transition-all duration-200 shadow-sm hover:shadow-card-hover group"
          aria-label="Sign in with Google"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span class="font-medium text-gray-700 group-hover:text-gray-900 transition-colors">Sign in with Google</span>
        </button>

        <button
          @click="authStore.loginWithFacebook"
          class="w-full flex items-center justify-center gap-3 px-6 py-3 bg-[#1877F2] text-white rounded-lg hover:bg-[#166FE5] transition-all duration-200 shadow-sm hover:shadow-card-hover"
          aria-label="Sign in with Facebook"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          <span class="font-medium">Sign in with Facebook</span>
        </button>

        <button
          @click="authStore.loginWithGitHub"
          class="w-full flex items-center justify-center gap-3 px-6 py-3 bg-[#24292f] text-white rounded-lg hover:bg-[#32383f] transition-all duration-200 shadow-sm hover:shadow-card-hover"
          aria-label="Sign in with GitHub"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
          </svg>
          <span class="font-medium">Sign in with GitHub</span>
        </button>

        <button
          @click="authStore.loginWithDiscord"
          class="w-full flex items-center justify-center gap-3 px-6 py-3 bg-[#5865F2] text-white rounded-lg hover:bg-[#4752C4] transition-all duration-200 shadow-sm hover:shadow-card-hover"
          aria-label="Sign in with Discord"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
          </svg>
          <span class="font-medium">Sign in with Discord</span>
        </button>
      </div>

      <p class="mt-8 text-sm text-gray-400">
        Access is by invitation only. Contact the owner for an invite code.
      </p>

      <div class="mt-4 pt-4 border-t border-cabin-sand">
        <p class="text-xs text-gray-400">
          <router-link to="/privacy" class="hover:text-primary-600 transition-colors">Privacy Policy</router-link>
          <span class="mx-2 text-cabin-warm">|</span>
          <router-link to="/data-deletion" class="hover:text-primary-600 transition-colors">Data Deletion</router-link>
        </p>
      </div>
    </div>
  </div>
</template>
